<!--
*  TTTech nerve-ui-components
*  Copyright(c) 2023. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <div>
    <v-col xxl="8" xl="8" lg="8" md="12" sm="12" xs="12" cols="12" class="pl-0">
      <div class="d-flex justify-end">
        <div v-if="isSearchFieldExists">
          <v-text-field
            id="iiotWorkloadActivityLogsSearchInput"
            v-model="search"
            :label="$t('nerveSimpleTable.search')"
            :disabled="loading"
            clearable
            prepend-inner-icon="mdi-magnify"
            hide-details
            class="search-width font-class-name"
            @keydown.enter="onSearch(search)"
            @click:clear="onSearch('')"
          />
        </div>
        <div class="pa-2">
          <v-row>
            <v-tooltip v-if="auditLogPermission" bottom>
              <template #activator="{ on, attrs }">
                <v-img
                  id="iiotWorkloadActivityLogsViewActivityLogsButton"
                  :src="'/img/link_to_os.svg'"
                  max-height="28px"
                  max-width="28px"
                  v-bind="attrs"
                  class="mt-1 mr-1 pointer"
                  v-on="on"
                  @click="viewActivityLogs"
                />
              </template>
              <span>{{ $t('nodes.activityLogs.viewActivityLogsDescription') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-img
                  :id="
                    loading ? 'iiotWorkloadActivityLogsRefreshButtonDisabled' : 'iiotWorkloadActivityLogsRefreshButton'
                  "
                  :src="loading ? '/img/refresh_disabled_button.svg' : '/img/refresh_button.svg'"
                  max-height="28px"
                  max-width="28px"
                  v-bind="attrs"
                  class="mt-1 mr-1 pointer"
                  v-on="on"
                  @click="onRefresh"
                />
              </template>
              <span>{{ $t('nodes.activityLogs.refresh') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-img
                  :id="
                    loading
                      ? 'iiotWorkloadActivityLogsDownloadButtonDisabled'
                      : 'iiotWorkloadActivityLogsDownloadButton'
                  "
                  :src="loading ? '/img/download_disabled.svg' : '/img/download.svg'"
                  max-height="28px"
                  max-width="28px"
                  v-bind="attrs"
                  class="mt-1 ml-1 pointer"
                  v-on="on"
                  @click="onDownload"
                />
              </template>
              <span>{{ $t('nodes.activityLogs.download') }}</span>
            </v-tooltip>
          </v-row>
        </div>
      </div>
    </v-col>
    <v-row v-if="activityLogs.length || loading" id="iiotWorkloadActivityLogsList">
      <v-col xxl="8" xl="8" lg="8" md="12" sm="12" xs="12" cols="12">
        <div
          ref="logsContainer"
          :class="{ 'logs-div-bcg-color': activityLogs.length }"
          class="logs-div"
          @scroll="onScroll"
        >
          <div class="pa-8">
            <v-row v-for="(log, index) of activityLogs" :key="index" class="font-14 truncate">
              <v-col
                v-for="(column, i) in columnsInfo"
                :key="i"
                :cols="column.width"
                class="pa-1"
                @mouseover="isTooltip($event)"
              >
                <v-tooltip bottom :disabled="!tooltip">
                  <template #activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <div
                        :class="{
                          'error-text-color': log && log.result && log.result.toLowerCase() === 'failed',
                        }"
                      >
                        {{ log[column.name] }}
                      </div>
                    </div>
                  </template>
                  <span>{{ log[column.name] }}</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </div>
        </div>
        <v-progress-linear
          v-if="loading"
          id="iiotWorkloadActivityLogsProgressLinear"
          indeterminate
          color="primary"
          class="mb-10"
        ></v-progress-linear>
      </v-col>
    </v-row>
  </div>
</template>
<script>
export default {
  name: 'NerveWorkloadLogs',
  props: {
    activityLogs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    columnsInfo: {
      type: Array,
      default: () => [],
    },
    limitPerPage: {
      type: Number,
      default: () => 50,
    },
    auditLogPermission: {
      type: Boolean,
      default: () => false,
    },
    count: {
      type: Number,
      default: () => 200,
    },
  },
  data() {
    return {
      search: '',
      tooltip: true,
      page: 1,
      enteredSearch: '',
      // related to new requirement search field has been removed from activity log table
      // if we decide to include search field again in the future, we can do it by setting
      // isSearchFieldExists property on true
      isSearchFieldExists: false,
    };
  },
  methods: {
    onDownload() {
      if (this.loading) {
        return;
      }
      this.$emit('download-activity-logs');
    },
    onRefresh() {
      if (this.loading) {
        return;
      }
      this.$emit('load-more-activity-logs', { page: 1, limit: this.limitPerPage, search: this.enteredSearch });
      this.page = 1;
    },
    onSearch(search) {
      if (this.loading) {
        return;
      }
      this.page = 1;
      this.enteredSearch = search;
      this.getActivityLogs();
    },

    // tooltip will be shown only in the case when text is too big and when contains 3 dots
    isTooltip(event) {
      this.tooltip = event.target.scrollWidth > event.target.offsetWidth;
    },
    onScroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (scrollTop + clientHeight >= scrollHeight && !this.loading && this.count > this.activityLogs.length) {
        this.page += 1;
        this.getActivityLogs();
      }
    },
    getActivityLogs() {
      this.$emit('load-more-activity-logs', {
        page: this.page,
        limit: this.limitPerPage,
        search: this.enteredSearch,
      });
    },

    resetNumberOfPage() {
      this.page = 1;
    },

    viewActivityLogs() {
      this.$emit('view-activity-logs-for-selected-workload');
    },
  },
};
</script>

<style lang="scss" scoped>
.primary-tttech-background-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}

.logs-div {
  max-height: 63vh;
  width: 100%;
  overflow: auto;
}

::-webkit-scrollbar {
  width: 3px;
  height: 3px;
  background-color: silver;
}

::-webkit-scrollbar-thumb {
  @extend .primary-tttech-background-btn;
}

.logs-div-bcg-color {
  background-color: rgb(217, 217, 217, 0.2);
}

.font-14 {
  font-size: 14px;
}

.search-width {
  width: 250px;
}

.truncate {
  max-width: 100%;

  v-col {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.error-text-color {
  color: #fe2020;
}

.font-class-name {
  font-size: 16px !important;
}
</style>
